/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { TeamsInRegion } from "@microsoft/shifts-models-data";
import { ListItem } from "@microsoft/shifts-models-view";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { DataService } from "sh-services/dataservice/DataService";

import { CrossLocationTeamDataProvider } from "./CrossLocationTeamDataProvider";
import { CrossLocationTeamDataProviderV2 } from "./CrossLocationTeamDataProviderV2";
import { CrossLocationTeamMapper } from "./CrossLocationTeamMapper";
import { ICrossLocationTeamDataService } from "./ICrossLocationTeamDataService";

/**
 * The data service for cross-location teams.
 */
export class CrossLocationTeamDataService extends DataService implements ICrossLocationTeamDataService {
    /**
     * The latest team id requested if any, else undefined.
     * This is to prevent same network call to be performed multiple times while data is loading.
     * Note: this happened when multiple React component reads the UI state and the network call is loading,
     * while the state has not yet been updated by mobx.
     */
    private latestTeamIdRequested: string | undefined;

    /**
     * Gets the cross-location teams for the current user.
     * @param teamId The team id.
     * @param setDataInState The callback to set the view models data in state.
     * @returns The cross-location teams.
     * @deprecated Use getCrossLocationTeamsV2 instead.
     */
    public async getCrossLocationTeams(teamId: string, setDataInState: (teams: ListItem[]) => void): Promise<ListItem[]> {
        if (teamId == this.latestTeamIdRequested) {
            return [];
        }

        this.latestTeamIdRequested = teamId;

        const mapper = new CrossLocationTeamMapper();

        const setDataInMemory = (teams: TeamsInRegion): void => {
            setDataInState(mapper.toListItem(teams));
            this.latestTeamIdRequested = undefined;
        };

        const provider = new CrossLocationTeamDataProvider({
            database: this.crossLocationTeamDatabase,
            setDataInMemory,
            teamId,
            tenantId: this.tenantId,
            userId: this.loggedInUserId
        });

        const teams = await this.getData<TeamsInRegion>(provider);

        return mapper.toListItem(teams);
    }

    /**
     * Gets the cross-location teams for the current user.
     * Using GET /teams/{teamId}/crosslocation/publishfrom endpoint.
     * @param teamId The team id.
     * @param setDataInState The callback to set the view models data in state.
     * @returns The cross-location teams.
     */
    public async getCrossLocationTeamsV2(teamId: string, setDataInState: (teams: ListItem[]) => void): Promise<ListItem[]> {
        if (teamId == this.latestTeamIdRequested) {
            return [];
        }

        this.latestTeamIdRequested = teamId;

        const mapper = new CrossLocationTeamMapper();

        const setDataInMemory = (teams: TeamsInRegion): void => {
            setDataInState(mapper.toListItem(teams));
            this.latestTeamIdRequested = undefined;
        };

        const provider = new CrossLocationTeamDataProviderV2({
            database: this.crossLocationTeamDatabase,
            setDataInMemory,
            teamId,
            tenantId: this.tenantId,
            userId: this.loggedInUserId
        });

        const teams = await this.getData<TeamsInRegion>(provider);

        return mapper.toListItem(teams);
    }

    /**
     * Resets the sync state.
     */
    public async resetSyncState(): Promise<void> {
        return Promise.resolve();
    }
}
